import React, { useEffect, useState } from 'react';
import { Wallet, ArrowUpRight, ArrowDownRight, RefreshCw, Send, DollarSign, PiggyBank, TrendingUp } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AdminSystemWallets = () => {
  const [stats, setStats] = useState(null);
  const [maintenanceWallet, setMaintenanceWallet] = useState(null);
  const [promotionWallet, setPromotionWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [transferData, setTransferData] = useState({
    recipient_username: '',
    amount: '',
    wallet_type: 'cash_wallet'
  });
  const [promoAction, setPromoAction] = useState({ action: 'add', amount: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, maintenanceRes, promoRes] = await Promise.all([
        api.get('/admin/wallet/wallet-stats'),
        api.get('/admin/wallet/maintenance-wallet'),
        api.get('/admin/wallet/promotion-wallet')
      ]);
      setStats(statsRes.data);
      setMaintenanceWallet(maintenanceRes.data);
      setPromotionWallet(promoRes.data);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/wallet/maintenance-wallet/transfer?recipient_username=${transferData.recipient_username}&amount=${transferData.amount}&wallet_type=${transferData.wallet_type}`);
      toast.success('Transfer successful!');
      setShowTransferModal(false);
      setTransferData({ recipient_username: '', amount: '', wallet_type: 'cash_wallet' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  const handlePromoUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/wallet/promotion-wallet/update?action=${promoAction.action}&amount=${promoAction.amount}`);
      toast.success(`Promotion wallet ${promoAction.action}ed successfully!`);
      setShowPromoModal(false);
      setPromoAction({ action: 'add', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-system-wallets-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold">System Wallets</h1>
            <p className="text-muted-foreground mt-2">Manage Maintenance & Promotion Wallets</p>
          </div>
          <button onClick={fetchData} className="p-3 bg-white/10 rounded-xl hover:bg-white/20">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* System Wallet Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Maintenance Wallet */}
          <div className="glass-card p-6 border-2 border-orange-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <PiggyBank className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Maintenance Wallet</h3>
                  <p className="text-xs text-muted-foreground">Transfer charges collected</p>
                </div>
              </div>
            </div>
            <p className="text-4xl font-bold text-orange-500 mb-4">
              ₹{(maintenanceWallet?.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
            <button
              onClick={() => setShowTransferModal(true)}
              className="w-full py-3 bg-orange-500/20 text-orange-500 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-orange-500/30"
            >
              <Send className="w-4 h-4" />
              Transfer to User
            </button>
          </div>

          {/* Promotion Wallet */}
          <div className="glass-card p-6 border-2 border-green-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Global Promotion Wallet</h3>
                  <p className="text-xs text-muted-foreground">7% commission pool (all members)</p>
                </div>
              </div>
            </div>
            <p className="text-4xl font-bold text-green-500 mb-4">
              ₹{(promotionWallet?.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
            <button
              onClick={() => setShowPromoModal(true)}
              className="w-full py-3 bg-green-500/20 text-green-500 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-green-500/30"
            >
              <DollarSign className="w-4 h-4" />
              Add / Deduct Funds
            </button>
          </div>
        </div>

        {/* User Wallet Totals */}
        <div className="glass-card p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">User Wallet Totals</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Total Cash Wallets</p>
              <p className="text-2xl font-bold text-primary">
                ₹{(stats?.user_wallets?.total_cash || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Total Commission Wallets</p>
              <p className="text-2xl font-bold text-accent">
                ₹{(stats?.user_wallets?.total_commission || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground mb-1">Total User Promotion Wallets</p>
              <p className="text-2xl font-bold text-green-500">
                ₹{(stats?.user_wallets?.total_promotion || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        {/* Recent Transfer Charges */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold mb-4">Recent Transfer Charges</h2>
          {maintenanceWallet?.recent_charges?.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {maintenanceWallet.recent_charges.map((tx, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div>
                    <p className="font-semibold">@{tx.sender_username} → @{tx.recipient_username}</p>
                    <p className="text-sm text-muted-foreground">
                      Transfer: ₹{tx.amount} | Charge: ₹{tx.charge}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-orange-500 font-semibold">+₹{tx.charge?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No transfer charges yet</p>
          )}
        </div>

        {/* Transfer Modal */}
        {showTransferModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Transfer from Maintenance Wallet</h2>
              <form onSubmit={handleTransfer} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Recipient Username</label>
                  <input
                    type="text"
                    value={transferData.recipient_username}
                    onChange={(e) => setTransferData({ ...transferData, recipient_username: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Enter username"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={transferData.amount}
                    onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Credit to Wallet</label>
                  <select
                    value={transferData.wallet_type}
                    onChange={(e) => setTransferData({ ...transferData, wallet_type: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  >
                    <option value="cash_wallet">Cash Wallet</option>
                    <option value="commission_wallet">Commission Wallet</option>
                    <option value="promotion_wallet">Promotion Wallet</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">Transfer</button>
                  <button
                    type="button"
                    onClick={() => setShowTransferModal(false)}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Promotion Wallet Modal */}
        {showPromoModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Update Promotion Wallet</h2>
              <p className="text-muted-foreground mb-4">Current Balance: ₹{(promotionWallet?.balance || 0).toFixed(2)}</p>
              <form onSubmit={handlePromoUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Action</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setPromoAction({ ...promoAction, action: 'add' })}
                      className={`flex-1 py-3 rounded-xl font-semibold ${promoAction.action === 'add' ? 'bg-green-500 text-white' : 'bg-white/5'}`}
                    >
                      Add Funds
                    </button>
                    <button
                      type="button"
                      onClick={() => setPromoAction({ ...promoAction, action: 'deduct' })}
                      className={`flex-1 py-3 rounded-xl font-semibold ${promoAction.action === 'deduct' ? 'bg-red-500 text-white' : 'bg-white/5'}`}
                    >
                      Deduct Funds
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={promoAction.amount}
                    onChange={(e) => setPromoAction({ ...promoAction, amount: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {promoAction.action === 'add' ? 'Add' : 'Deduct'} ₹{promoAction.amount || 0}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPromoModal(false)}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSystemWallets;
